import React, { useEffect, useState } from 'react';
import { connect } from '../../../redux/store';
import {
  actions as portfolioActions,
  selectIsPortfolioOwner,
} from '../../../redux/modules/settingsPortfolio';
import { actions as serviceActions } from '../../../redux/modules/settingsService';
import { UncontrolledTooltip } from 'reactstrap';
import { I18n } from '@kineticdata/react';
import { Link } from '@reach/router';
import { PageTitle } from '../../shared/PageTitle';
import {
  Card,
  CardCol,
  CardRow,
  EmptyMessage,
  ErrorMessage,
  LoadingMessage,
  TimeAgo,
  Utils,
  C,
  addToast,
  addToastAlert,
  openConfirm,
  openModalForm,
  selectServicesKappSlug,
} from '@kineticdata/bundle-common';
import {
  isActiveClass,
  getActionPath,
  getCatalogRevisionLockData,
} from '../../../utils';
import { ActionCard } from '../../actions/ActionCard';
import { ContentManager } from '../../shared/content_manager/ContentManager';
import { NewServiceModal } from './NewModals';
import {
  CONTENT_MANAGER_MODE,
  getStatusColor,
} from '../../shared/content_manager/helpers';
import { List, Map } from 'immutable';

const PortfolioComponent = ({
  portfolioId,
  mode,
  // Selected portfolio
  portfolio,
  portfolioContentReady,
  portfolioError,
  portfolioLoading,
  // List of all revisions for the selected portfolio id
  revisions,
  // Actions for the selected portfolio
  actions,
  // All portfolios
  portfoliosList,
  portfoliosNameMap,
  // Services for this portfolio
  services,
  servicesList,
  servicesInit,
  servicesLoading,
  servicesError,
  // Are user actions pending
  actionsPending,
  serviceActionsPending,
  // Actions
  fetchPortfolio,
  clonePortfolio,
  expirePortfolio,
  deletePortfolioRevision,
  fetchServices,
  createService,
  // Other props
  kappSlug,
  profile,
  isOwner,
  navigate,
  appLocation,
  isMobile,
}) => {
  // State for managing visibility of modal for creating new services for this portfolio
  const [newModalOpen, setNewModalOpen] = useState(false);

  // Fetch the current portfolio on mount
  useEffect(
    () => {
      fetchPortfolio(portfolioId);
    },
    [fetchPortfolio, portfolioId],
  );

  const isPortfolioDraft =
    !!portfolio &&
    portfolio.values &&
    portfolio.values[C.FIELD_STATUS] === 'Draft';
  const isPortfolioActive =
    !!portfolio &&
    portfolio.values &&
    portfolio.values[C.FIELD_STATUS] === 'Active';
  const doesPortfolioHaveChildren =
    !!portfolio && portfolio.children && portfolio.children.length > 0;

  const handleCreateChangeRequest = () =>
    openModalForm({
      formSlug: C.FORM_SLUG_CATALOG_CHANGE_REQUEST,
      kappSlug: kappSlug,
      title: 'Request Change',
      values: {
        'Catalog ID': portfolioId,
        'Catalog Type': 'portfolio',
        'Catalog Submission ID': portfolio.id,
        'Assigned Teams': portfolio.values['Technical Owners'],
      },
      confirmationMessage: 'Your change request has been submitted.',
      duration: 5000,
    });

  const handleDeletePortfolio = () => {
    // If there is a revision, show alert that revsision needs to first be deleted
    if (revisions.size > 0 && !isPortfolioDraft) {
      addToastAlert({
        severity: 'warning',
        title: 'Cannot delete portfolio with existing revisions',
        message:
          'Please delete any draft revisions or wait for pending revisions to be completed before deleting the portfolio.',
      });
    }
    // Otherwise delete or expire the portfolio
    else {
      openConfirm({
        title: 'Delete Portfolio',
        body: (
          <div>
            <span className="d-block mb-3">
              Are you sure you want to delete this portfolio?
            </span>
            <em>
              This action is immediate, does not go through an approval process,
              and cannot be undone.
            </em>
          </div>
        ),
        actionName: 'Delete',
        ok: () =>
          // If portfolio is a draft, delete it, otherwise expire it
          isPortfolioDraft
            ? deletePortfolioRevision({
                submissionId: portfolio.id,
                cancel: doesPortfolioHaveChildren,
                success: submission => {
                  navigate(`..${!!mode ? '/..' : ''}`);
                  addToast('Portfolio successfully deleted');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to delete portfolio',
                    message: error.message,
                  });
                },
              })
            : expirePortfolio({
                submissionId: portfolio.id,
                success: submission => {
                  navigate(`..${!!mode ? '/..' : ''}`);
                  addToast('Portfolio successfully deleted');
                },
                failure: error => {
                  addToastAlert({
                    severity: 'danger',
                    title: 'Failed to delete portfolio',
                    message: error.message,
                  });
                },
              }),
      });
    }
  };

  const handleInactivatePortfolio = () => {
    openConfirm({
      title: 'Inactivate Portfolio',
      body: (
        <div>
          <span className="d-block mb-3">
            Are you sure you want to inactivate this portfolio? This will remove
            the active record and{' '}
            {revisions.size > 0
              ? 'leave the current revision in its place.'
              : 'create a new revision record in draft status.'}
          </span>
          <em>
            This action is immediate, does not go through an approval process,
            and cannot be undone.
          </em>
        </div>
      ),
      actionName: 'Inactivate',
      ok: () =>
        expirePortfolio({
          submissionId: portfolio.id,
          clone: revisions.size === 0,
          refetchItem: true,
          success: submission => {
            navigate(!!mode ? '..' : '');
            addToast('Portfolio successfully inactivated');
          },
          failure: error => {
            addToastAlert({
              severity: 'danger',
              title: 'Failed to inactivate portfolio',
              message: error.message,
            });
          },
        }),
    });
  };

  return (
    <div className="page-container">
      <div className="page-panel">
        <PageTitle
          className="page-title-heading--sm"
          parts={[portfolio && portfolio.values['Name'], 'Catalog Management']}
          suptitle="PORTFOLIO"
          title={portfolio && portfolio.values['Name']}
          breadcrumbs={[
            {
              label: (
                <span>
                  <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                  <I18n>{C.SETTINGS_LABEL}</I18n>
                </span>
              ),
              to: '/settings',
            },
            {
              label: 'Catalog Management',
              to: `${appLocation}/settings/catalog/portfolios`,
            },
          ]}
          actions={
            !portfolioError &&
            portfolio && [
              {
                icon: !portfolioLoading ? 'sync' : 'sync fa-spin',
                aria: 'Reload Portfolio',
                onClick: () => {
                  fetchPortfolio(portfolioId);
                  fetchServices();
                },
              },
              profile.isSMO && {
                label: 'Request Change',
                onClick: handleCreateChangeRequest,
                menu: true,
              },
              profile.isSMO && {
                label: 'Create New Service',
                onClick: () => setNewModalOpen(true),
                disabled: serviceActionsPending || newModalOpen,
                menu: true,
              },
              isOwner &&
                revisions.size === 0 && {
                  menu: true,
                  label: 'Start Revision',
                  onClick: () =>
                    clonePortfolio({
                      submissionId: portfolio.id,
                      success: submission => {
                        navigate(`revision/${submission.id}`);
                      },
                      failure: error => {
                        addToastAlert({
                          severity: 'danger',
                          title: 'Failed to start a revision',
                          message: error.message,
                        });
                      },
                    }),
                  disabled: actionsPending,
                },
              profile.isSMO &&
                isPortfolioActive && {
                  label: 'Inactivate Portfolio',
                  onClick: handleInactivatePortfolio,
                  className: 'text-danger',
                  disabled: actionsPending,
                  menu: true,
                  divider: true,
                },
              profile.isSMO && {
                label: 'Delete Portfolio',
                onClick: handleDeletePortfolio,
                className: 'text-danger',
                disabled: actionsPending,
                menu: true,
                divider: !isPortfolioActive,
              },
            ]
          }
        />

        {!!newModalOpen && (
          <NewServiceModal
            toggle={() => setNewModalOpen(false)}
            createService={createService}
            onSuccess={submission =>
              navigate(
                `${!!mode ? '../' : ''}services/${
                  submission.values[C.FIELD_SERVICE_ID]
                }`,
              )
            }
            portfoliosList={portfoliosList}
            portfolioId={portfolioId}
          />
        )}

        {portfolioError ? (
          <ErrorMessage
            title="Failed to retrieve portfolio."
            message={portfolioError.message}
          />
        ) : !portfolioContentReady && portfolioLoading ? (
          <LoadingMessage />
        ) : (
          !!portfolio && (
            <>
              {portfolio.values['Status'] !== 'Active' && (
                <div className="alert alert-info alert-bar mb-3">
                  <I18n>
                    This is a new portfolio that is not yet published.
                  </I18n>{' '}
                  {isOwner && (
                    <I18n>
                      Go to the revision tab to continue editing this portfolio.
                    </I18n>
                  )}
                </div>
              )}

              <ul className="nav nav-tabs h6" role="tablist">
                <li role="tab" className="nav-item">
                  <Link
                    to={!mode ? '.' : '..'}
                    getProps={isActiveClass('nav-link')}
                  >
                    <I18n>Portfolio</I18n>
                  </Link>
                </li>
                <li role="tab" className="nav-item">
                  <Link
                    to={!mode ? 'services' : '../services'}
                    getProps={isActiveClass('nav-link')}
                  >
                    <I18n>Services</I18n>
                  </Link>
                </li>
                {revisions.size > 0 && (
                  <li role="tab" className="nav-item">
                    <Link
                      to={!mode ? 'revision' : '../revision'}
                      getProps={isActiveClass('nav-link')}
                    >
                      <I18n>Revision</I18n>
                    </Link>
                  </li>
                )}
                {actions.size > 0 && (
                  <li role="tab" className="nav-item">
                    <Link
                      to={!mode ? 'actions' : '../actions'}
                      getProps={isActiveClass('nav-link')}
                    >
                      <I18n>Change Requests</I18n>
                    </Link>
                  </li>
                )}
              </ul>

              {!mode && (
                <div className="nav-tabs--content">
                  {portfolio && portfolioContentReady ? (
                    <ContentManager
                      submission={portfolio}
                      mode={CONTENT_MANAGER_MODE.MANAGE}
                      username={profile.username}
                    />
                  ) : (
                    <ErrorMessage title="Failed to retrieve portfolio content." />
                  )}
                </div>
              )}

              {mode === 'services' && (
                <div className="nav-tabs--content">
                  {servicesError ? (
                    <ErrorMessage
                      title="Failed to retrieve services."
                      message={servicesError.message}
                    />
                  ) : !servicesInit && servicesLoading ? (
                    <LoadingMessage />
                  ) : servicesList.size === 0 ? (
                    <EmptyMessage title="There are no services for this portfolio." />
                  ) : (
                    <div className="scroll-wrapper-h">
                      <table className="table table--settings">
                        <thead>
                          <tr>
                            <th>
                              <I18n>Service Name</I18n>
                            </th>
                            <th>
                              <I18n>Status</I18n>
                            </th>
                            <th>
                              <I18n>Type</I18n>
                            </th>
                            <th>
                              <I18n>Portfolios</I18n>
                            </th>
                            <th>
                              <I18n>Last Updated</I18n>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {servicesList.map(service => {
                            return (
                              <tr key={service.id}>
                                <td>
                                  <Link to={service.values[C.FIELD_SERVICE_ID]}>
                                    {service.values['Name']}
                                  </Link>
                                </td>
                                <td>
                                  {service.values['Status']}
                                  {service.values['Status'] === 'Active' &&
                                  !!services
                                    .get(service.values[C.FIELD_SERVICE_ID])
                                    .find(
                                      p => p.values['Status'] !== 'Active',
                                    ) ? (
                                    <>
                                      {' \u00B7 '}
                                      <I18n>In Revision</I18n>
                                    </>
                                  ) : null}
                                </td>
                                <td>{service.values['Type']}</td>
                                <td className="multi">
                                  {(
                                    service.values[C.FIELD_PORTFOLIO_ID] || []
                                  ).map(
                                    pId =>
                                      portfoliosNameMap.has(pId) ? (
                                        <Link
                                          key={pId}
                                          to={`${
                                            !!mode ? '../..' : '..'
                                          }/${pId}`}
                                        >
                                          <I18n>
                                            {portfoliosNameMap.get(pId) || pId}
                                          </I18n>
                                        </Link>
                                      ) : null,
                                  )}
                                </td>
                                <td>
                                  {<TimeAgo timestamp={service.updatedAt} />}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {mode === 'revision' && (
                <div className="nav-tabs--content">
                  {revisions.size > 0 ? (
                    <div className="cards mb-4">
                      {revisions.map(r => {
                        const approval = List(r.children)
                          .filter(c => c.type === C.APPROVAL_FORM_TYPE)
                          .sortBy(a => a.updatedAt)
                          .reverse()
                          .first();
                        const isRevisionOwner = selectIsPortfolioOwner(
                          profile,
                          r,
                        );

                        // Get lock info for revision
                        const {
                          isLocked,
                          isLockedByMe,
                        } = getCatalogRevisionLockData(r, profile.username);

                        return (
                          <Card key={r.id}>
                            <CardCol>
                              <CardRow type="title">
                                {isLocked &&
                                  !isLockedByMe && (
                                    <>
                                      <span
                                        id={`lock-${r.id}`}
                                        className="fa fa-lock fa-fw"
                                      />
                                      <UncontrolledTooltip
                                        placement="right"
                                        target={`lock-${r.id}`}
                                        delay={0}
                                        trigger="hover focus"
                                      >
                                        <I18n>
                                          This revision is currently locked by
                                          another user.
                                        </I18n>
                                      </UncontrolledTooltip>
                                    </>
                                  )}
                                <span>
                                  <I18n>Revision</I18n> {r.handle}
                                </span>
                                <div className="ml-auto">
                                  <Link className="btn btn-primary" to={r.id}>
                                    <I18n>
                                      {r.values['Status'] === 'Draft' &&
                                      isRevisionOwner
                                        ? 'Continue Editing'
                                        : 'Review Revision'}
                                    </I18n>
                                    <span className="fa fa-arrow-right fa-fw" />
                                  </Link>
                                </div>
                              </CardRow>
                              <CardRow className="text-muted" type="meta">
                                <dl className="flat align-items-center">
                                  <div>
                                    <dd>
                                      <span
                                        className={`badge badge-${getStatusColor(
                                          r.values['Status'],
                                        )} badge-stylized text-muted`}
                                      >
                                        <I18n>{r.values['Status']}</I18n>
                                      </span>
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>
                                      <I18n>Created</I18n>
                                    </dt>
                                    <dd>
                                      <TimeAgo timestamp={r.createdAt} />
                                    </dd>
                                  </div>
                                  {r.coreState === 'Submitted' ? (
                                    <div>
                                      <dt>
                                        <I18n>Submitted</I18n>
                                      </dt>
                                      <dd>
                                        <TimeAgo timestamp={r.submittedAt} />
                                      </dd>
                                    </div>
                                  ) : (
                                    <div>
                                      <dt>
                                        <I18n>Updated</I18n>
                                      </dt>
                                      <dd>
                                        <TimeAgo timestamp={r.updatedAt} />
                                      </dd>
                                    </div>
                                  )}
                                </dl>
                              </CardRow>
                              {approval && (
                                <CardRow
                                  className="text-muted mt-1"
                                  type="meta"
                                >
                                  <dl className="flat align-items-center">
                                    {approval.coreState === 'Submitted' && (
                                      <div>
                                        <dt>
                                          <I18n>Approval Decision</I18n>
                                        </dt>
                                        <dd>
                                          <I18n>
                                            {approval.values['Approved']}
                                          </I18n>
                                        </dd>
                                      </div>
                                    )}
                                    {approval.values[
                                      C.FIELD_ASSIGNED_INDIVIDUAL_NAME
                                    ] && (
                                      <div>
                                        <dt>
                                          <I18n>Approvers</I18n>
                                        </dt>
                                        <dd>
                                          <I18n>
                                            {
                                              approval.values[
                                                C.FIELD_ASSIGNED_INDIVIDUAL_NAME
                                              ]
                                            }
                                          </I18n>
                                        </dd>
                                      </div>
                                    )}
                                    {approval &&
                                      approval.values[
                                        C.FIELD_ASSIGNED_TEAM_NAME
                                      ] && (
                                        <div>
                                          <dt>
                                            <I18n>Approval Teams</I18n>
                                          </dt>
                                          <dd>
                                            <I18n>
                                              {
                                                approval.values[
                                                  C.FIELD_ASSIGNED_TEAM_NAME
                                                ]
                                              }
                                            </I18n>
                                          </dd>
                                        </div>
                                      )}
                                    {approval.coreState === 'Submitted' &&
                                      approval.values['Approved'] ===
                                        'Denied' && (
                                        <div>
                                          <dt>
                                            <I18n>Reason</I18n>
                                          </dt>
                                          <dd>
                                            <I18n>
                                              {approval.values['Reason']}
                                            </I18n>
                                          </dd>
                                        </div>
                                      )}
                                  </dl>
                                </CardRow>
                              )}
                            </CardCol>
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyMessage title="A revision does not exist." />
                  )}
                </div>
              )}

              {mode === 'actions' && (
                <div className="nav-tabs--content">
                  {actions.size > 0 ? (
                    <div className="cards mb-4">
                      {actions.map(action => {
                        return (
                          <ActionCard
                            key={action.id}
                            submission={action}
                            path={getActionPath(appLocation, action)}
                            reload={() => {
                              fetchPortfolio(portfolioId);
                            }}
                            external={true}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyMessage title="There are no change requests." />
                  )}
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const portfolio =
    state.settingsPortfolio.current &&
    state.settingsPortfolio.current.values[C.FIELD_PORTFOLIO_ID] ===
      props.portfolioId
      ? state.settingsPortfolio.current
      : null;
  const portfolios =
    state.settingsPortfolio.data.get(props.portfolioId) || List();
  return {
    // Current portfolio
    portfolio,
    portfolioContentReady: portfolio && portfolio.values['Content'],
    portfolioLoading: state.settingsPortfolio.currentLoading,
    portfolioError: state.settingsPortfolio.currentError,
    // Revisions for this portfolio
    revisions: portfolios.filter(p => p.values['Status'] !== 'Active'),
    // Actions tied to this portfolio that the user can access
    actions: List(
      portfolio && portfolio.children
        ? portfolio.children.filter(
            c =>
              c.type === 'Action' &&
              (state.app.profile.isSMO ||
                c.values[C.FIELD_ASSIGNED_TEAMS].some(t =>
                  Utils.isMemberOf(state.app.profile, t),
                )),
          )
        : [],
    ),
    // All portfolios
    portfoliosList: state.settingsPortfolio.dataList,
    portfoliosNameMap: state.settingsPortfolio.dataList.reduce(
      (map, p) => map.set(p.values[C.FIELD_PORTFOLIO_ID], p.values['Name']),
      Map(),
    ),
    // Services for this portfolio
    services: state.settingsService.data,
    servicesList: state.settingsService.dataList.filter(s =>
      (s.values[C.FIELD_PORTFOLIO_ID] || []).includes(props.portfolioId),
    ),
    servicesInit: state.settingsService.dataInit,
    servicesLoading: state.settingsService.dataLoading,
    servicesError: state.settingsService.dataError,
    // Are any user actions are pending
    actionsPending: state.settingsPortfolio.actionsPending > 0,
    serviceActionsPending: state.settingsService.actionsPending > 0,
    // Other state
    kappSlug: selectServicesKappSlug(state),
    profile: state.app.profile,
    isOwner: selectIsPortfolioOwner(state.app.profile, portfolio),
    appLocation: state.app.location,
    layoutSize: state.app.layoutSize,
    isMobile: ['small', 'medium'].includes(state.app.layoutSize),
  };
};

const mapDispatchToProps = {
  fetchPortfolio: portfolioActions.fetchPortfolioRequest,
  clonePortfolio: portfolioActions.clonePortfolioRequest,
  expirePortfolio: portfolioActions.expirePortfolioRequest,
  deletePortfolioRevision: portfolioActions.deletePortfolioRevisionRequest,
  fetchServices: serviceActions.fetchServicesRequest,
  createService: serviceActions.createServiceRequest,
};

export const Portfolio = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PortfolioComponent);
